import React from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import {
  ActionList,
  BreadcrumbBar,
  IBreadcrumbBarItem,
  IIconTextLinkProps,
  Head,
  myAccountLink,
} from "component-library";

import { FacetCategory } from "shared/models/facet";
import { IFollowUp } from "shared/models/followUp";
import { MapDispatchToProps } from "shared/types/utilities";

import { MY_FILTERS_FULL_PATH, USE_BROWSER_BACK_KEY } from "config/common";
import PageKey from "config/PageKey";

import { IApplicationState } from "store";
import { getFacets } from "store/facet/facet.actions";
import { FacetStoreKey, GetFacets } from "store/facet/facet.types";
import { getFollowUpPage } from "store/page/page.actions";
import { GetFollowUpPage } from "store/page/page.types";
import { getPageTranslation } from "store/translation/translation.actions";
import { GetPageTranslation } from "store/translation/translation.types";
import { hidePopup, setLoading, showPopup } from "store/ui/ui.actions";
import { HidePopup, SetLoading, ShowPopup } from "store/ui/ui.types";

import { IActionList, IActionListItem } from "models/common";
import { JobItemType } from "models/job";

import copyPrefixedObjectKeys from "helpers/copyPrefixedObjectKeys";
import jobService, { SetJobActionType } from "services/api/job/jobService";

import { GTMEvents } from "app-shell/GoogleTagManager/GTMEvents";

import withPageData, {
  ILoaders,
  IWithPageDataProps,
  mapStateToWithPageDataProps,
} from "hoc/withPageData";

import FollowUpView from "components/FollowUpView/FollowUpView";

import {
  ActionListItemKey,
  prepareActionList,
  prepareBreadcrumbs,
  prepareTexts,
} from "./FollowUpPage.helpers";

import "./FollowUpPage.scss";
import withRouter, { RouterProps } from "hoc/withRouter";

export interface IFollowUpPageTexts {
  title: string;
  sentBy: string;
  breakdownInfo: string;
  breakdownTitle: string;
  pageTitle: string;
}

export interface IFollowUpPageActions {
  getPageTranslation: GetPageTranslation;
  setLoading: SetLoading;
  showPopup: ShowPopup;
  hidePopup: HidePopup;
  getFacets: GetFacets;
  getFollowUpPage: GetFollowUpPage;
}

export interface IFollowUpPageMatchParams {
  breakdownId: string;
  followUpId: string;
}

export interface IFollowUpPageStoreProps {
  breadcrumbs: IBreadcrumbBarItem[];
  texts: IFollowUpPageTexts;
  followUp: IFollowUp;
  markAsRead: SetJobActionType;
  actionList: IActionList;
}

export interface IFollowUpPageProps
  extends IFollowUpPageActions,
    IFollowUpPageStoreProps,
    IWithPageDataProps,
    RouterProps {}

interface IFollowUpPageState {
  returnUri?: string;
  [USE_BROWSER_BACK_KEY]?: boolean;
}

export class FollowUpPage extends React.PureComponent<
  IFollowUpPageProps,
  IFollowUpPageState
> {
  private readonly actionListHandlers: {
    [ActionListItemKey.BackToJobsFeed](): void;
  };

  readonly state: Readonly<IFollowUpPageState> = {};

  constructor(props: IFollowUpPageProps) {
    super(props);

    const actionListHandlers = {
      [ActionListItemKey.BackToJobsFeed]: this.handleBackClick,
    };
    this.actionListHandlers = copyPrefixedObjectKeys(
      actionListHandlers,
      "more-dropdown_"
    );
  }

  public componentDidMount() {
    void this.markAsRead();
    this.setReturnUri();
  }

  public render() {
    const { breadcrumbs, texts, followUp, userType, actionList } = this.props;

    return (
      <section className="c-follow-up-page">
        <Head registerPageView={this.registerPageView}>
          <title>{this.props.texts.pageTitle}</title>
        </Head>
        <BreadcrumbBar items={breadcrumbs} />
        <FollowUpView
          message={followUp.body}
          userType={userType}
          texts={texts}
          breakdownId={followUp.linkmailId}
          actionList={
            <div>
              <ActionList
                items={this.prepareActionListItems()}
                moreButtonText={actionList.moreButtonText}
                variant="pageHeader"
              />
            </div>
          }
        />
      </section>
    );
  }

  private prepareActionListItems(): IIconTextLinkProps[] {
    return this.props.actionList.items?.map(
      (item: IActionListItem): IIconTextLinkProps => ({
        ...item,
        handleClick: this.createHandlerActionListItemClick(item.id),
      })
    );
  }

  private handleBackClick = () => {
    const { router } = this.props;
    const { returnUri, [USE_BROWSER_BACK_KEY]: useBrowserBack } = this.state;

    if (useBrowserBack) {
      router.navigate(-1);
    } else {
      router.navigate(returnUri || MY_FILTERS_FULL_PATH);
    }
  };

  private createHandlerActionListItemClick =
    (id: string = "") =>
    (e: React.MouseEvent) => {
      e.preventDefault();

      this.actionListHandlers[id]?.();
    };

  private async markAsRead() {
    const id = jobService.generateInteractionItemId(
      JobItemType.FollowUp,
      this.props.followUp.id
    );
    try {
      await this.props.markAsRead([{ id, jobItemType: JobItemType.FollowUp }]);
    } catch (e) {
      console.error(e);
    }
  }

  private registerPageView = () => {
    GTMEvents.jobsFeed.pageViews.followUp(this.props.texts.pageTitle);
  };

  private setReturnUri() {
    const { router } = this.props;
    const [searchParams, setSearchParams] = router.searchParams;

    this.setState({
      returnUri: searchParams.get("returnUri") || "",
      [USE_BROWSER_BACK_KEY]: !!router.location.state?.from,
    });
    setSearchParams("", { replace: true });
  }
}

async function loadExternalData(props: IFollowUpPageProps) {
  return Promise.all([
    props.getFacets([FacetCategory.ProductionType], props.locale),
  ]);
}

const pageDataLoaders: ILoaders<IFollowUpPageProps> = {
  loadTranslation(props, isLocaleChanged) {
    return props.getPageTranslation(
      { pageKey: PageKey.FollowUp, locale: props.locale },
      { throwOnError: isLocaleChanged }
    );
  },
  async loadData(props: IFollowUpPageProps) {
    const { breakdownId = "", followUpId = "" } = props.router.params;

    await loadExternalData(props);

    return props.getFollowUpPage(
      breakdownId.toLowerCase(),
      followUpId.toLowerCase()
    );
  },
  loadOnLocaleChange: loadExternalData,
};

const mapStateToProps = (
  state: IApplicationState,
  props: IFollowUpPageProps
): IFollowUpPageStoreProps => {
  const pageTranslation = state.translation.pages[PageKey.FollowUp]!;
  const { common } = state.translation;
  const followUp = state.page.data!.followUp!;
  const productionTypes = state.facet[FacetStoreKey.ProductionType]!;

  return {
    breadcrumbs: prepareBreadcrumbs(
      pageTranslation,
      myAccountLink,
      props.router.navigate
    ),
    texts: prepareTexts(pageTranslation, common, followUp, productionTypes),
    actionList: prepareActionList(pageTranslation),
    followUp,
    markAsRead: (...args) => jobService.markAsRead(...args),
  };
};

const mapDispatchToProps: MapDispatchToProps<IFollowUpPageActions> = {
  getPageTranslation,
  setLoading,
  showPopup,
  hidePopup,
  getFacets,
  getFollowUpPage,
};

export default compose(
  withRouter,
  connect(mapStateToWithPageDataProps, mapDispatchToProps),
  withPageData<IFollowUpPageProps>(PageKey.FollowUp, pageDataLoaders),
  connect(mapStateToProps, mapDispatchToProps)
)(FollowUpPage) as React.ComponentType;
